'use es6';
/**
 * @typedef {Object} NormalizedLayoutData
 * @property {Object}   widgets       - Key/value collection of LayoutWidget models by widget id. <String, LayoutWidget>
 * @property {Object}   rows          - Key/value collection of widgets by row id. <String, Array[String]>
 * @property {String}   rootWidgetId  - Id of layout's root widget.
 */

import LinkedListNode from 'DesignData/lib/collections/LinkedListNode';
import { someNormalizedWidgets } from 'DesignData/lib/normalization/layouts/iterators';
import { createRowId, getOrCreateWidgetIdFromWidget } from 'DesignData/lib/normalization/layouts/identifiers';
import { isSectionDelimiterType } from 'DesignData/helpers/widgets';
import { Layout, LayoutWidget, UnifiedLayout } from 'DesignData/models/layouts';
import { stripDuplicateClasses } from 'DesignData/lib/css';
import WidgetCssEmailFields from 'DesignData/constants/WidgetCssEmailFields';
const WIDGET_EMAIL_SIZING_CSS_FIELDS = [WidgetCssEmailFields.CSS_BORDER_WIDTH, WidgetCssEmailFields.CSS_MARGIN_BOTTOM, WidgetCssEmailFields.CSS_MARGIN_LEFT, WidgetCssEmailFields.CSS_MARGIN_RIGHT, WidgetCssEmailFields.CSS_MARGIN_TOP, WidgetCssEmailFields.CSS_PADDING_BOTTOM, WidgetCssEmailFields.CSS_PADDING_LEFT, WidgetCssEmailFields.CSS_PADDING_RIGHT, WidgetCssEmailFields.CSS_PADDING_TOP].map(__field => __field.toLowerCase()); // Add fields (string name) you do not want sent up to the server here.

const LAYOUT_WIDGET_FRONTEND_ONLY_FIELDS = [];

const clone = obj => JSON.parse(JSON.stringify(obj));
/**
 * Sort numerically.
 */


function numericSorter(a, b) {
  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
}
/**
 * Converts a normalized layout back into a tree structure.
 *
 * @param {Object.<LayoutWidget>} widgets        Normalized immutable widgets.
 * @param {Object}                rows           Normalized immutable rows.
 * @param {String}                widgetId       Id of widget to use as root.
 * @param {Object}                options
 * @param {Function}              options.nodeModifier
 * @param {Number}                depth          Private - used for recursion.
 * @return {Object} Layout tree.
 */


export function createLayoutTreeFromNormalizedData(widgets, rows, widgetId, options, depth = 0) {
  const {
    nodeModifier
  } = options || {};
  const widget = widgets[widgetId].without(LAYOUT_WIDGET_FRONTEND_ONLY_FIELDS).asMutable({
    deep: true
  });
  widget.rows = widget.rows.map(rowId => {
    return rows[rowId].asMutable().reduce((indexedRow, __widgetId) => {
      const __widget = createLayoutTreeFromNormalizedData(widgets, rows, __widgetId, options, depth + 1);

      if (nodeModifier) {
        nodeModifier(__widget);
      }

      indexedRow[__widget.x] = __widget;
      return indexedRow;
    }, {});
  });

  if (depth === 0) {
    Layout.convertRegularLayoutNodesToFlexNodes(widget);
  }

  return widget;
}
export function getWidgetAncestors(widgets, rows, parentWidgetId, widgetId) {
  const parentWidget = widgets[parentWidgetId];
  if (!parentWidget.rows.length) return [];

  for (const _rowId of parentWidget.rows) {
    for (const _widgetId of rows[_rowId]) {
      const _widget = widgets[_widgetId];

      if (_widgetId === widgetId) {
        return [_widget];
      }

      const ancestors = getWidgetAncestors(widgets, rows, _widgetId, widgetId);

      if (ancestors.length) {
        return [_widget].concat(ancestors);
      }
    }
  }

  return [];
}
/**
 * Creates a hash map detailing widget ancestry for a layout tree.
 *
 * @param {Object} layoutData
 * @returns {Object.<String, LinkedListNode>}
 */

export function getWidgetAncestry(layoutData) {
  const {
    widgets,
    rows,
    rootWidgetId
  } = layoutData;
  const listsByWidgetId = {};
  someNormalizedWidgets(widgets, rows, rootWidgetId, (widget, {
    rowId,
    parentId
  }) => {
    listsByWidgetId[widget.id] = new LinkedListNode({
      next: listsByWidgetId[parentId] || null,
      data: {
        widgetId: widget.id,
        widget,
        parentId,
        parent: widgets[parentId] || null,
        rowId,
        isRoot: widget.id === rootWidgetId
      }
    });
    return false;
  });
  return listsByWidgetId;
}
/**
 * Normalizes a layout tree (layoutData) into widgets, rows, and a rootWidgetId.
 *
 * @param {Object}   layoutTree             Layout tree to be converted.
 * @param {Object}   options
 * @param {Function} options.nodeModifier   Function to apply modifications to a node. Pass by reference.
 */

export function getNormalizedLayoutDataFromTree(layoutTree, {
  nodeModifier = null
} = {}) {
  if (!layoutTree) {
    return {};
  } // We need to deeply mutate the layout tree. Data as returned from server is inconsistent, is missing properties, etc.


  const rootWidget = clone(layoutTree); // Convert inconsistent nodes (flex) to regular cell structure to work with bootstrap grid.

  UnifiedLayout.convertFlexNodesToRegularLayoutNodes(rootWidget);
  UnifiedLayout.forEach(rootWidget, widget => {
    // Ensure the widget has an id.
    widget.id = getOrCreateWidgetIdFromWidget(widget); // Ensure duplicate CSS classes are not present.

    if (widget.params.css_class) {
      widget.params.css_class = stripDuplicateClasses(widget.params.css_class);
    } // Ensure section delimiters do not have children.
    // When our APIs were converted from Python to Java, there was a bug that copied
    // modules into section delimiter's rows[]. The bug was fixed in early 2016 but
    // the bad data can still be found in old or revision templates.


    if (isSectionDelimiterType(widget.type)) {
      widget.rows = [];
    } // Ensure "px" is stripped from email CSS sizing fields
    // See https://issues.hubspotcentral.com/browse/CG-7813


    WIDGET_EMAIL_SIZING_CSS_FIELDS.forEach(__field => {
      const value = widget.params[__field] || '';

      if (value && !/^\d+(\.\d*)?$/.test(value)) {
        widget.params[__field] = value.toString().replace(/[^0-9.-]/g, '');
      }
    });

    if (nodeModifier) {
      nodeModifier(widget);
    }
  }); // Recurse each node, placing it in the widgets dictionary and updating refs in `rows` properties.
  // For each nested row of rows, place it in the rows collection.

  const widgets = {};
  const rows = {};
  UnifiedLayout.forEach(rootWidget, widget => {
    const copy = Object.assign({}, widget);

    if (!copy.rows) {
      // Make sure every widget has a rows collection.
      copy.rows = [];
    } else {
      copy.rows = copy.rows.map(__row => {
        const row = Object.keys(__row).map(Number).sort(numericSorter).map(idx => {
          const rowWidget = __row[idx];
          return rowWidget.id;
        });
        const rowId = createRowId();
        rows[rowId] = row;
        return rowId;
      });
    }

    widgets[widget.id] = new LayoutWidget(copy);
  });
  const rootWidgetId = rootWidget.id;
  return {
    widgets,
    rows,
    rootWidgetId
  };
}
/**
 * Normalizes a layout into widgets, rows, and a rootWidgetId
 *
 * @param {Object}   layout                 Layout to be converted
 * @param {Object}   options
 * @param {Function} options.nodeModifier   Function to apply modifications to a node. Pass by reference.
 * @return {NormalizedLayoutData}
 */

export function getNormalizedLayoutData(layout, options) {
  return getNormalizedLayoutDataFromTree(layout && layout.layoutData, options);
}